import React, { useState } from 'react';
import { Lock, User, Mail, UserPlus, AlertCircle, CheckCircle, ArrowRight, Shield, Globe, Battery, BarChart3, Activity } from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import EmailVerificationPending from './EmailVerificationPending';

const LoginWithSignup: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signupStep, setSignupStep] = useState(1);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const { signIn, signUp, useDemoAuth, resetPassword } = useSupabaseAuth();

  // Move useDemoAuth call outside of handleDemoLogin to fix React hooks rule
  const demoAuth = useDemoAuth;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    
    if (isSignUp) {
      // Validation for sign up
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setIsLoading(false);
        return;
      }
      
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        setIsLoading(false);
        return;
      }
      
      const result = await signUp(email, password, fullName);
      if (!result.success) {
        setError(result.error || 'Failed to sign up');
      } else {
        // Check if email confirmation is required
        setVerificationEmail(email);
        setShowVerificationPending(true);
      }
    } else {
      const result = await signIn(email, password);
      if (!result.success) {
        // Check if error is due to email not confirmed
        if (result.error?.includes('Email not confirmed')) {
          setVerificationEmail(email);
          setShowVerificationPending(true);
        } else {
          setError(result.error || 'Invalid email or password');
        }
      }
    }
    
    setIsLoading(false);
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    try {
      await demoAuth();
    } catch (error) {
      console.error('Demo login failed:', error);
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setIsLoading(true);
    
    const result = await resetPassword(resetEmail);
    if (result.success) {
      setResetMessage(result.message || 'Password reset instructions sent to your email');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetMessage('');
      }, 5000);
    } else {
      setError(result.error || 'Failed to send reset email');
    }
    
    setIsLoading(false);
  };

  // Show email verification pending screen if needed
  if (showVerificationPending) {
    return (
      <EmailVerificationPending 
        email={verificationEmail} 
        onBackToLogin={() => {
          setShowVerificationPending(false);
          setError('');
          setIsSignUp(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-traqpack-yellow via-traqpack-yellow to-yellow-400 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-2xl mb-4 shadow-lg transform hover:scale-105 transition-transform">
            <Globe className="w-10 h-10 text-traqpack-yellow" />
          </div>
          <h1 className="text-6xl font-black text-black mb-4 tracking-tight">TraqPack</h1>
          <p className="text-xl text-black font-medium leading-relaxed">
            {isSignUp ? 'Join the Future of Asset Tracking' : 'Welcome Back to TraqPack'}
          </p>
          <p className="text-lg text-black/70 mt-2">
            {isSignUp ? 'Create your account to start tracking' : 'Track what matters, with precision'}
          </p>
        </div>

        {/* Auth Form */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-black/5">
          {signupStep === 2 && isSignUp ? (
            // Success state
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to TraqPack!</h2>
              <p className="text-gray-600 mb-8">Your account has been created successfully.</p>
              <div className="space-y-3 max-w-sm mx-auto">
                <div className="flex items-center gap-3 text-left">
                  <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <p className="text-sm text-gray-700">Check your email for confirmation</p>
                </div>
                <div className="flex items-center gap-3 text-left">
                  <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <p className="text-sm text-gray-700">Claim your first device with its IMEI</p>
                </div>
                <div className="flex items-center gap-3 text-left">
                  <div className="w-8 h-8 bg-traqpack-yellow/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">3</span>
                  </div>
                  <p className="text-sm text-gray-700">Start tracking in real-time</p>
                </div>
              </div>
              <div className="mt-8">
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                  Signing you in...
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isSignUp ? 'Create Your Account' : 'Sign In to TraqPack'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setError('');
                    setSuccessMessage('');
                    setSignupStep(1);
                  }}
                  className="text-sm text-traqpack-yellow hover:text-yellow-700 font-medium transition-colors"
                >
                  {isSignUp ? 'Already have an account?' : 'Create account'}
                </button>
              </div>

              {successMessage && !isSignUp && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {isSignUp && (
                  <div className="animate-slide-up">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 placeholder-gray-400 transition-all"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 placeholder-gray-400 transition-all"
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 placeholder-gray-400 transition-all"
                      placeholder={isSignUp ? "Create a strong password" : "Enter your password"}
                      required
                    />
                  </div>
                  {isSignUp && password.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className={`h-1 flex-1 rounded-full transition-all ${
                          password.length >= 8 ? 'bg-green-500' : 'bg-gray-300'
                        }`} />
                        <span className="text-xs text-gray-600">8+ characters</span>
                      </div>
                    </div>
                  )}
                </div>

                {isSignUp && (
                  <div className="animate-slide-up">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 placeholder-gray-400 transition-all"
                        placeholder="Confirm your password"
                        required
                      />
                      {confirmPassword && (
                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                          {password === confirmPassword ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2 animate-shake">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {isSignUp && (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Your data is encrypted and secure</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      By creating an account, you agree to TraqPack's Terms of Service and Privacy Policy
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || (isSignUp && password !== confirmPassword)}
                  className="w-full bg-black text-traqpack-yellow font-bold py-4 px-6 rounded-xl hover:bg-gray-900 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3 shadow-lg"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-traqpack-yellow"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {isSignUp ? <UserPlus className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                      {isSignUp ? 'Create Account' : 'Sign In'}
                    </>
                  )}
                </button>

                {!isSignUp && (
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-traqpack-yellow hover:text-yellow-700 font-medium transition-colors"
                    >
                      Forgot your password?
                    </button>
                  </div>
                )}
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleDemoLogin}
              disabled={isLoading}
              className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Activity className="w-4 h-4" />
              Try Demo Account
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Experience TraqPack with sample data
            </p>
          </div>
        </div>

        {/* Features Preview */}
        <div className="mt-8 text-center">
          <p className="text-black/60 text-sm font-medium mb-4 tracking-wide uppercase">
            {isSignUp ? 'What you get with TraqPack' : 'Trusted by businesses worldwide'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-black/10 hover:border-black/20 transition-all group">
              <Globe className="w-8 h-8 text-black mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-bold text-black">Global Coverage</p>
              <p className="text-xs text-black/60 mt-1">Track anywhere, anytime</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-black/10 hover:border-black/20 transition-all group">
              <Shield className="w-8 h-8 text-black mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-bold text-black">Bank-Level Security</p>
              <p className="text-xs text-black/60 mt-1">Your data is protected</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-black/10 hover:border-black/20 transition-all group">
              <Battery className="w-8 h-8 text-black mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-bold text-black">10-Year Battery</p>
              <p className="text-xs text-black/60 mt-1">Set it and forget it</p>
            </div>
            <div className="bg-white/80 backdrop-blur rounded-xl p-4 border border-black/10 hover:border-black/20 transition-all group">
              <BarChart3 className="w-8 h-8 text-black mb-2 group-hover:scale-110 transition-transform" />
              <p className="text-sm font-bold text-black">Real-Time Analytics</p>
              <p className="text-xs text-black/60 mt-1">Instant insights</p>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Reset Your Password</h2>
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            
            {resetMessage && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{resetMessage}</span>
              </div>
            )}
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-traqpack-yellow focus:bg-white focus:outline-none text-gray-900 placeholder-gray-400 transition-all"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setError('');
                    setResetMessage('');
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !resetEmail}
                  className="flex-1 bg-black text-traqpack-yellow font-bold py-3 px-4 rounded-xl hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-traqpack-yellow"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Reset Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginWithSignup;